import { AIProvider } from './aiProvider';
import { hashSource, id, nowIso } from './hash';
import { decidePublication } from './policy';
import { blockingIssues, runDeterministicQA, validateSecurityEntities } from './quality';
import { ingestionPolicy, localeDisplayNames, sampleFinding, targetLocales } from './seed';
import { findConceptsInText } from './terminology';
import {
  AssistantResult,
  AuditEvent,
  IngestedFieldResult,
  LocaleCode,
  Localization,
  LocalizationState,
  QualityResult,
  ReviewItem,
  SourceContent,
  TranslationMemoryEntry,
  WebsiteVariant,
} from './types';

function audit(type: string, message: string, partial: Partial<AuditEvent> = {}): AuditEvent {
  return {
    id: id('audit'),
    type,
    lane: 'STATIC_UI',
    message,
    timestamp: nowIso(),
    ...partial,
  };
}

function latestTerminologyVersion(source: SourceContent, state: LocalizationState): number {
  const concepts = findConceptsInText(source.sourceText, state.terminology);
  return concepts.reduce((version, concept) => Math.max(version, concept.version), 1);
}

function upsertMemory(
  memory: TranslationMemoryEntry[],
  source: SourceContent,
  localization: Localization,
  origin: TranslationMemoryEntry['origin'],
): TranslationMemoryEntry[] {
  const existing = memory.find((entry) => entry.sourceHash === source.sourceHash && entry.locale === localization.locale);
  if (existing) {
    return memory.map((entry) => (entry.id === existing.id ? {
      ...entry,
      translatedString: localization.localizedText,
      origin,
      approvalStatus: 'APPROVED',
      usageCount: entry.usageCount + 1,
      qualityScore: localization.qualityScore,
      updatedAt: nowIso(),
    } : entry));
  }
  return [
    ...memory,
    {
      id: id('tm'),
      sourceHash: source.sourceHash,
      sourceString: source.sourceText,
      locale: localization.locale,
      translatedString: localization.localizedText,
      origin,
      approvalStatus: 'APPROVED',
      usageCount: 1,
      qualityScore: localization.qualityScore,
      updatedAt: nowIso(),
    },
  ];
}

export async function processSourceUpdate(
  previous: LocalizationState,
  sourceId: string,
  nextText: string,
  provider: AIProvider,
): Promise<LocalizationState> {
  const source = previous.sources.find((item) => item.id === sourceId);
  if (!source) return previous;
  const updatedSource: SourceContent = {
    ...source,
    sourceText: nextText,
    updatedAt: nowIso(),
    sourceHash: hashSource(nextText),
    riskLevel: await provider.classifyRisk(nextText, source.context),
  };

  let state: LocalizationState = {
    ...previous,
    sources: previous.sources.map((item) => (item.id === sourceId ? updatedSource : item)),
    pipelineStage: 'SOURCE CHANGE',
    auditEvents: [
      audit('SOURCE_UPDATED', `${updatedSource.key} changed and emitted a localization event.`, {
        lane: 'STATIC_UI',
        sourceContentId: sourceId,
      }),
      ...previous.auditEvents,
    ],
  };

  const localizations: Localization[] = [...state.localizations.filter((item) => item.sourceContentId !== sourceId)];
  const qualityResults: QualityResult[] = [...state.qualityResults];
  const reviewItems: ReviewItem[] = [...state.reviewItems.filter((item) => {
    const loc = state.localizations.find((localization) => localization.id === item.localizationId);
    return loc?.sourceContentId !== sourceId;
  })];
  let translationMemory = [...state.translationMemory];
  const events: AuditEvent[] = [];
  const detectedConcepts = await provider.detectTerminology(updatedSource.sourceText, state.terminology);
  events.push(audit('TERMINOLOGY_DETECTED', `${detectedConcepts.length} terminology concept(s) detected.`, {
    lane: 'STATIC_UI',
    sourceContentId: sourceId,
  }));

  for (const localeCode of targetLocales) {
    const locale = state.locales.find((item) => item.code === localeCode);
    if (!locale) continue;
    const memoryHit = translationMemory.find((entry) => (
      entry.sourceHash === updatedSource.sourceHash
      && entry.locale === localeCode
      && entry.approvalStatus === 'APPROVED'
    ));

    let candidateText = '';
    let providerName = provider.name;
    let model = 'tm-approved-exact-match';
    let promptVersion = 'tm-reuse';
    let tmReused = false;
    events.push(audit('TRANSLATION_STARTED', `${locale.displayName} processing started.`, {
      lane: 'STATIC_UI',
      locale: localeCode,
      sourceContentId: sourceId,
      status: 'PROCESSING',
    }));

    if (memoryHit) {
      candidateText = memoryHit.translatedString;
      tmReused = true;
      translationMemory = translationMemory.map((entry) => (entry.id === memoryHit.id ? { ...entry, usageCount: entry.usageCount + 1 } : entry));
      events.push(audit('TM_REUSED', `${locale.displayName} reused an approved exact TM match.`, {
        lane: 'STATIC_UI',
        locale: localeCode,
        sourceContentId: sourceId,
      }));
    } else {
      const response = await provider.translate({
        sourceText: updatedSource.sourceText,
        locale: localeCode,
        context: updatedSource.context,
        riskLevel: updatedSource.riskLevel,
        terminology: detectedConcepts,
      });
      candidateText = response.text;
      providerName = response.provider;
      model = response.model;
      promptVersion = response.promptVersion;
      events.push(audit('TRANSLATION_COMPLETED', `${locale.displayName} candidate generated by ${model}.`, {
        lane: 'STATIC_UI',
        locale: localeCode,
        sourceContentId: sourceId,
      }));
    }

    const deterministicChecks = runDeterministicQA(updatedSource, candidateText, localeCode, detectedConcepts);
    const semantic = await provider.evaluateTranslation(updatedSource.sourceText, candidateText, localeCode);
    const localizationId = id('loc');
    const qualityResult: QualityResult = {
      id: id('quality'),
      localizationId,
      deterministicChecks,
      semantic,
      overallScore: semantic.overallScore,
      blockingIssues: blockingIssues(deterministicChecks),
      warnings: deterministicChecks.filter((check) => !check.blocking && !check.passed).map((check) => check.message ?? check.name),
      evaluator: `${provider.name}:semantic-qe`,
      timestamp: nowIso(),
    };
    qualityResults.push(qualityResult);
    events.push(audit('QUALITY_CHECKED', `${locale.displayName} scored ${semantic.overallScore}.`, {
      lane: 'STATIC_UI',
      locale: localeCode,
      sourceContentId: sourceId,
    }));

    const decision = decidePublication(updatedSource.riskLevel, locale, deterministicChecks, semantic);
    const localization: Localization = {
      id: localizationId,
      sourceContentId: sourceId,
      locale: localeCode,
      localizedText: candidateText,
      status: decision.status,
      provider: providerName,
      model,
      qualityScore: semantic.overallScore,
      terminologyVersion: latestTerminologyVersion(updatedSource, state),
      promptVersion,
      generatedAt: nowIso(),
      publishedAt: decision.status === 'PUBLISHED' ? nowIso() : undefined,
      humanOverride: false,
      tmReused,
      riskLevel: updatedSource.riskLevel,
      qualityResultId: qualityResult.id,
    };
    localizations.push(localization);

    if (decision.status === 'PUBLISHED') {
      translationMemory = upsertMemory(translationMemory, updatedSource, localization, tmReused ? 'CACHE' : 'AI');
      events.push(audit('AUTO_PUBLISHED', `${locale.displayName} auto-published.`, {
        lane: 'STATIC_UI',
        locale: localeCode,
        sourceContentId: sourceId,
        status: 'PUBLISHED',
      }));
    } else {
      reviewItems.push({
        id: id('review'),
        localizationId,
        reason: decision.reason,
        severity: updatedSource.riskLevel,
        sourceText: updatedSource.sourceText,
        candidateTranslation: candidateText,
        locale: localeCode,
        context: updatedSource.context,
        qualityResultId: qualityResult.id,
        terminologyIssues: qualityResult.blockingIssues.filter((issue) => issue.includes('Terminology')),
        status: 'OPEN',
        createdAt: nowIso(),
      });
      events.push(audit('QUARANTINED', `${locale.displayName} routed to review: ${decision.reason}`, {
        lane: 'STATIC_UI',
        locale: localeCode,
        sourceContentId: sourceId,
        status: 'REVIEW_REQUIRED',
      }));
    }
  }

  return {
    ...state,
    localizations,
    qualityResults,
    reviewItems,
    translationMemory,
    auditEvents: [...events.reverse(), ...state.auditEvents],
    pipelineStage: 'PUBLISH / REVIEW',
  };
}

export function approveReview(
  previous: LocalizationState,
  reviewId: string,
  finalValue: string,
  notes = 'Approved in MVP review queue.',
): LocalizationState {
  const review = previous.reviewItems.find((item) => item.id === reviewId);
  if (!review) return previous;
  const localization = previous.localizations.find((item) => item.id === review.localizationId);
  if (!localization) return previous;
  const source = previous.sources.find((item) => item.id === localization.sourceContentId);
  if (!source) return previous;
  const updatedLocalization: Localization = {
    ...localization,
    localizedText: finalValue,
    status: 'PUBLISHED',
    humanOverride: finalValue !== review.candidateTranslation,
    publishedAt: nowIso(),
  };
  return {
    ...previous,
    localizations: previous.localizations.map((item) => (item.id === localization.id ? updatedLocalization : item)),
    translationMemory: upsertMemory(previous.translationMemory, source, updatedLocalization, 'HUMAN'),
    reviewItems: previous.reviewItems.map((item) => (item.id === reviewId ? {
      ...item,
      status: 'APPROVED',
      reviewerNotes: notes,
      finalApprovedValue: finalValue,
      resolvedAt: nowIso(),
    } : item)),
    auditEvents: [
      audit(finalValue !== review.candidateTranslation ? 'HUMAN_OVERRIDE' : 'REVIEW_APPROVED', `Review approved for ${localization.locale}; translation published and TM updated.`, {
        lane: 'STATIC_UI',
        locale: localization.locale,
        sourceContentId: source.id,
        status: 'APPROVED',
      }),
      ...previous.auditEvents,
    ],
  };
}

export function rejectReview(previous: LocalizationState, reviewId: string): LocalizationState {
  const review = previous.reviewItems.find((item) => item.id === reviewId);
  if (!review) return previous;
  return {
    ...previous,
    reviewItems: previous.reviewItems.map((item) => (item.id === reviewId ? { ...item, status: 'REJECTED', resolvedAt: nowIso() } : item)),
    localizations: previous.localizations.map((item) => (item.id === review.localizationId ? { ...item, status: 'FAILED' } : item)),
    auditEvents: [audit('REVIEW_REJECTED', 'Review item rejected and localization marked failed.', { lane: 'STATIC_UI', status: 'REJECTED' }), ...previous.auditEvents],
  };
}

const severityLabels: Record<LocaleCode, Record<string, string>> = {
  en: { Critical: 'Critical' },
  ja: { Critical: '重大' },
  fr: { Critical: 'Critique' },
  de: { Critical: 'Kritisch' },
  es: { Critical: 'Crítica' },
  'pt-BR': { Critical: 'Crítica' },
  ar: { Critical: 'حرج' },
};

export async function processFinding(previous: LocalizationState, provider: AIProvider, locale: LocaleCode): Promise<LocalizationState> {
  const results: IngestedFieldResult[] = [];
  const events: AuditEvent[] = [];
  const sourceFields = sampleFinding as unknown as Record<string, string>;
  for (const fieldPolicy of ingestionPolicy) {
    const value = sourceFields[fieldPolicy.field];
    if (fieldPolicy.policy === 'NEVER_TRANSLATE' || fieldPolicy.policy === 'PRESERVE_SOURCE') {
      results.push({ field: fieldPolicy.field, originalValue: value, localizedValue: value, locale, action: 'not-translated', provenance: fieldPolicy.rationale });
      continue;
    }
    if (fieldPolicy.policy === 'LOCALIZE_ENUM') {
      results.push({ field: fieldPolicy.field, originalValue: value, localizedValue: severityLabels[locale][value] ?? value, locale, action: 'enum-localized', provenance: 'Locale enum catalog.' });
      continue;
    }
    const source: SourceContent = {
      id: `ingested_${fieldPolicy.field}`,
      key: `finding.${fieldPolicy.field}`,
      sourceType: 'INGESTED_DATA',
      sourceText: value,
      sourceLocale: 'en',
      context: fieldPolicy.rationale,
      feature: 'Security Findings',
      component: 'FindingPayload',
      riskLevel: fieldPolicy.policy === 'TRANSLATE_HIGH_RISK' ? 'HIGH' : 'MEDIUM',
      createdAt: nowIso(),
      updatedAt: nowIso(),
      sourceHash: hashSource(value),
    };
    const tmHit = previous.translationMemory.find((entry) => entry.sourceHash === source.sourceHash && entry.locale === locale);
    if (tmHit) {
      results.push({ field: fieldPolicy.field, originalValue: value, localizedValue: tmHit.translatedString, locale, action: 'reused', provenance: 'Cache/TM hit from content hash.' });
    } else {
      const translation = await provider.translate({ sourceText: value, locale, context: fieldPolicy.rationale, riskLevel: source.riskLevel, terminology: findConceptsInText(value, previous.terminology) });
      results.push({
        field: fieldPolicy.field,
        originalValue: value,
        localizedValue: translation.text,
        locale,
        action: fieldPolicy.policy === 'TRANSLATE_HIGH_RISK' ? 'review-required' : 'translated',
        provenance: `${translation.provider}/${translation.model}; original payload preserved.`,
      });
    }
  }
  events.push(audit('SOURCE_UPDATED', `Incoming finding ${sampleFinding.cve} enriched for ${localeDisplayNames[locale]}.`, { lane: 'INGESTED_DATA', locale }));
  return {
    ...previous,
    ingestedResults: results,
    auditEvents: [...events, ...previous.auditEvents],
  };
}

export async function generateAssistant(previous: LocalizationState, provider: AIProvider, locale: LocaleCode, question: string): Promise<LocalizationState> {
  const terminology = findConceptsInText(`${question} Critical vulnerability Remediation`, previous.terminology);
  const response = await provider.generateAssistantResponse({ locale, question, terminology });
  const validation = validateSecurityEntities('CVE-2026-12345 4.2.1 prod-db-07 /opt/example/bin/service --version', response.text);
  const result: AssistantResult = {
    locale,
    question,
    response: response.text,
    terminology: terminology.map((concept) => concept.canonicalEnglish),
    validation,
    provenance: `${response.provider}/${response.model}; prompt ${response.promptVersion}; generated locale-native.`,
  };
  return {
    ...previous,
    assistantResults: [result, ...previous.assistantResults.filter((item) => item.locale !== locale)],
    auditEvents: [audit('AI_CONTENT_VALIDATED', `Security Assistant response generated for ${localeDisplayNames[locale]}.`, { lane: 'AI_CONTENT', locale }), ...previous.auditEvents],
  };
}

export async function processWebsite(previous: LocalizationState, provider: AIProvider): Promise<LocalizationState> {
  const variants: WebsiteVariant[] = [];
  for (const locale of targetLocales) {
    const title = await provider.translate({ sourceText: previous.websiteEntry.title, locale, context: 'CMS homepage title', riskLevel: 'LOW', terminology: previous.terminology });
    const body = await provider.translate({ sourceText: previous.websiteEntry.body, locale, context: 'CMS homepage body', riskLevel: 'LOW', terminology: previous.terminology });
    const seo = await provider.translate({ sourceText: previous.websiteEntry.seoTitle, locale, context: 'SEO product title', riskLevel: 'MEDIUM', terminology: findConceptsInText(previous.websiteEntry.seoTitle, previous.terminology) });
    variants.push({
      locale,
      title: title.text,
      body: body.text,
      seoTitle: seo.text,
      slug: `/${locale.toLowerCase()}/${previous.websiteEntry.slug}`,
      status: previous.websiteEntry.body.toLowerCase().includes('guarantee') ? 'REVIEW_REQUIRED' : 'PUBLISHED',
      policy: 'Body copy can auto-publish when passing; product terminology is enforced; security claims route to review.',
    });
  }
  return {
    ...previous,
    websiteVariants: variants,
    auditEvents: [audit('CMS_CONTENT_LOCALIZED', 'CMS content-change event localized through the shared control plane.', { lane: 'WEBSITE' }), ...previous.auditEvents],
  };
}
